import React, { useState, useEffect } from 'react';
import api from '../../services/api';

const BUILTIN = new Set(['user_registration', 'recipe_scan', 'share_recipes', 'payment']);

const s = {
  wrap:   { padding: '28px 32px', maxWidth: 900 },
  h1:     { fontSize: '1.4rem', color: 'var(--admin-text)', fontFamily: 'Inter,sans-serif', fontWeight: 600, marginBottom: 6 },
  sub:    { fontSize: '.78rem', color: 'var(--admin-text-dim)', marginBottom: 28 },
  section:{ marginBottom: 40 },
  secRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 },
  secLbl: { fontSize: '.7rem', fontWeight: 600, color: 'var(--admin-text-dim)', textTransform: 'uppercase', letterSpacing: '.08em' },
  btn:    { background: 'var(--admin-btn-primary)', border: 'none', color: '#fff', borderRadius: 5, padding: '7px 16px', fontSize: '.75rem', fontWeight: 600, cursor: 'pointer' },
  card:   { background: 'var(--admin-bg)', border: '1px solid var(--admin-border)', borderRadius: 8, padding: '16px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 },
  badge:  (on) => ({ display: 'inline-block', borderRadius: 20, padding: '2px 10px', fontSize: '.65rem', fontWeight: 700, background: on ? 'color-mix(in srgb, var(--admin-success) 15%, transparent)' : 'color-mix(in srgb, var(--admin-text-dim) 12%, transparent)', color: on ? 'var(--admin-success)' : 'var(--admin-text-dim)', marginRight: 10 }),
  toggle: (on) => ({ width: 44, height: 24, borderRadius: 12, border: 'none', cursor: 'pointer', background: on ? 'var(--admin-btn-primary)' : 'var(--admin-text-dim)', position: 'relative', transition: 'background .2s', flexShrink: 0 }),
  knob:   (on) => ({ position: 'absolute', top: 3, left: on ? 23 : 3, width: 18, height: 18, borderRadius: '50%', background: '#fff', transition: 'left .2s' }),
  btnDel: { background: 'none', border: '1px solid var(--admin-danger)', color: 'var(--admin-danger)', borderRadius: 4, padding: '3px 9px', fontSize: '.68rem', cursor: 'pointer' },
  errBox: { background: 'color-mix(in srgb, var(--admin-danger) 10%, transparent)', border: '1px solid var(--admin-danger)', borderRadius: 6, padding: '10px 14px', color: 'var(--admin-danger)', fontSize: '.78rem', marginBottom: 20 },
  overlay:{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 },
  mbox:   { background: 'var(--admin-bg)', border: '1px solid var(--admin-border)', borderRadius: 10, padding: 28, width: 420 },
  lbl:    { display: 'block', fontSize: '.68rem', color: 'var(--admin-text-muted)', marginBottom: 4, marginTop: 12 },
  inp:    { width: '100%', background: 'var(--admin-bg-input)', border: '1px solid var(--admin-border)', borderRadius: 5, color: 'var(--admin-text)', padding: '7px 10px', fontSize: '.78rem', boxSizing: 'border-box' },
};

const EMPTY = { name: '', label: '', description: '', enabled: false };

export default function Modules() {
  const [modules, setModules]   = useState([]);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [saving, setSaving]     = useState(null);
  const [editPkg, setEditPkg]   = useState(null);
  const [modal, setModal]       = useState(false);
  const [form, setForm]         = useState(EMPTY);
  const [formErr, setFormErr]   = useState('');
  const [confirmDeleteModule, setConfirmDeleteModule] = useState(null);
  const [pkgSaveError, setPkgSaveError] = useState('');

  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true);
    setError('');
    try {
      const [md, pd] = await Promise.all([api.getAllModules(), api.getPackages()]);
      setModules(md.modules || []);
      setPackages(pd.packages || []);
    } catch (e) {
      setError('Fehler beim Laden: ' + (e.message || 'Unbekannt'));
    } finally {
      setLoading(false);
    }
  }

  async function handleToggle(mod) {
    setSaving(mod.name);
    try {
      await api.toggleModule(mod.name, !mod.enabled);
      setModules(prev => prev.map(m => m.name === mod.name ? { ...m, enabled: !m.enabled } : m));
    } catch (e) { setError(e.message); }
    finally { setSaving(null); }
  }

  async function handleDelete(mod) {
    setConfirmDeleteModule(null);
    try { await api.deleteModule(mod.name); await load(); }
    catch (e) { setError(e.message); }
  }

  async function handleCreate(e) {
    e.preventDefault();
    setFormErr('');
    const name = form.name.trim().toLowerCase().replace(/[^a-z0-9_]/g, '_');
    if (!name || !form.label.trim()) { setFormErr('Name und Label sind Pflicht'); return; }
    setSaving('__new__');
    try {
      await api.createModule({ ...form, name });
      setModal(false); setForm(EMPTY); await load();
    } catch (e) { setFormErr(e.message); }
    finally { setSaving(null); }
  }

  async function handleSavePkg() {
    if (!editPkg) return;
    setPkgSaveError('');
    try { await api.setPackageModules(editPkg.id, editPkg.modules); await load(); setEditPkg(null); }
    catch (e) { setPkgSaveError(e.message); }
  }

  if (loading) return <div style={{ padding: 32, color: 'var(--admin-text-dim)' }}>Lade...</div>;

  return (
    <div style={s.wrap}>
      <h1 style={s.h1}>Modul-Verwaltung</h1>
      <p style={s.sub}>Module global aktivieren/deaktivieren. Pakete legen fest welche Module ein Benutzer nutzen darf.</p>

      {error && <div style={s.errBox}>{error} <button onClick={load} style={{ marginLeft: 12, background: 'none', border: '1px solid var(--admin-danger)', color: 'var(--admin-danger)', borderRadius: 4, padding: '2px 8px', cursor: 'pointer', fontSize: '.72rem' }}>Neu laden</button></div>}

      {confirmDeleteModule && (
        <div style={{ ...s.errBox, display: 'flex', alignItems: 'center', gap: 12, justifyContent: 'space-between' }}>
          <span>Modul <strong>„{confirmDeleteModule.label}"</strong> wirklich löschen?</span>
          <div style={{ display: 'flex', gap: 8 }}>
            <button style={{ background: 'var(--admin-danger)', border: 'none', color: '#fff', borderRadius: 4, padding: '4px 12px', fontSize: '.72rem', cursor: 'pointer' }} onClick={() => handleDelete(confirmDeleteModule)}>Ja, löschen</button>
            <button style={{ background: 'none', border: '1px solid var(--admin-border)', color: 'var(--admin-text-muted)', borderRadius: 4, padding: '4px 12px', fontSize: '.72rem', cursor: 'pointer' }} onClick={() => setConfirmDeleteModule(null)}>Abbrechen</button>
          </div>
        </div>
      )}

      {/* Module */}
      <div style={s.section}>
        <div style={s.secRow}>
          <span style={s.secLbl}>Module ({modules.length})</span>
          <button style={s.btn} onClick={() => { setForm(EMPTY); setFormErr(''); setModal(true); }}>+ Neues Modul</button>
        </div>
        {modules.map(mod => (
          <div key={mod.name} style={s.card}>
            <div>
              <div style={{ color: 'var(--admin-text)', fontWeight: 600, fontSize: '.85rem' }}>{mod.label}</div>
              <div style={{ color: 'var(--admin-text-dim)', fontSize: '.72rem', marginTop: 2 }}>{mod.description}</div>
              <div style={{ marginTop: 6 }}>
                <span style={s.badge(mod.enabled)}>{mod.enabled ? 'AKTIV' : 'INAKTIV'}</span>
                <code style={{ fontSize: '.65rem', color: 'var(--admin-text-dim)' }}>{mod.name}</code>
                {BUILTIN.has(mod.name) && <span style={{ fontSize: '.6rem', color: 'var(--admin-text-dim)', marginLeft: 8 }}>eingebaut</span>}
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              {!BUILTIN.has(mod.name) && (
                <button style={s.btnDel} onClick={() => { setError(''); setConfirmDeleteModule(mod); }}>✕</button>
              )}
              <button style={s.toggle(mod.enabled)} onClick={() => handleToggle(mod)} disabled={saving === mod.name}>
                <span style={s.knob(mod.enabled)} />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Pakete */}
      <div style={s.section}>
        <div style={s.secRow}><span style={s.secLbl}>Pakete & enthaltene Module</span></div>
        {packages.map(pkg => (
          <div key={pkg.id} style={{ ...s.card, flexDirection: 'column', alignItems: 'flex-start', gap: 10 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
              <div>
                <span style={{ color: 'var(--admin-text)', fontWeight: 600, fontSize: '.85rem' }}>{pkg.label}</span>
                <span style={{ color: 'var(--admin-text-dim)', fontSize: '.75rem', marginLeft: 10 }}>
                  {pkg.price_monthly === 0 ? 'kostenlos' : `${pkg.price_monthly.toFixed(2)} €/Monat`} · Scans: {pkg.scan_limit === 0 ? '∞' : pkg.scan_limit}
                </span>
              </div>
              <button
                style={{ background: 'none', border: '1px solid var(--admin-border)', color: 'var(--admin-text-muted)', borderRadius: 5, padding: '4px 12px', fontSize: '.72rem', cursor: 'pointer' }}
                onClick={() => setEditPkg({ id: pkg.id, modules: [...pkg.modules] })}
              >
                Module bearbeiten
              </button>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {pkg.modules.length === 0
                ? <span style={{ color: 'var(--admin-text-dim)', fontSize: '.72rem' }}>Keine Module</span>
                : pkg.modules.map(m => (
                  <span key={m} style={{ background: 'var(--admin-bg-analysis)', color: 'var(--admin-field-name)', borderRadius: 4, padding: '2px 8px', fontSize: '.7rem' }}>{m}</span>
                ))
              }
            </div>
          </div>
        ))}
      </div>

      {/* Modal: Neues Modul */}
      {modal && (
        <div style={s.overlay} onClick={e => e.target === e.currentTarget && setModal(false)}>
          <div style={s.mbox}>
            <div style={{ color: 'var(--admin-text)', fontWeight: 600, marginBottom: 16 }}>🧩 Neues Modul anlegen</div>
            <form onSubmit={handleCreate}>
              <label style={{ ...s.lbl, marginTop: 0 }}>Schlüssel * <span style={{ color: 'var(--admin-text-dim)', fontWeight: 400 }}>(wird im Code verwendet)</span></label>
              <input style={{ ...s.inp, fontFamily: 'monospace' }} value={form.name} placeholder="z.B. pdf_export"
                onChange={e => setForm(p => ({ ...p, name: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') }))} />
              <label style={s.lbl}>Anzeigename *</label>
              <input style={s.inp} value={form.label} placeholder="z.B. PDF-Export"
                onChange={e => setForm(p => ({ ...p, label: e.target.value }))} />
              <label style={s.lbl}>Beschreibung</label>
              <input style={s.inp} value={form.description} placeholder="Kurze Beschreibung"
                onChange={e => setForm(p => ({ ...p, description: e.target.value }))} />
              <label style={{ ...s.lbl, display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input type="checkbox" checked={form.enabled} onChange={e => setForm(p => ({ ...p, enabled: e.target.checked }))} />
                Sofort aktivieren
              </label>
              {formErr && <div style={{ color: 'var(--admin-danger)', fontSize: '.73rem', marginTop: 8 }}>{formErr}</div>}
              <div style={{ display: 'flex', gap: 8, marginTop: 20, justifyContent: 'flex-end' }}>
                <button type="button" onClick={() => setModal(false)}
                  style={{ background: 'none', border: '1px solid var(--admin-border)', color: 'var(--admin-text-muted)', borderRadius: 5, padding: '7px 16px', fontSize: '.75rem', cursor: 'pointer' }}>Abbrechen</button>
                <button type="submit" disabled={saving === '__new__'}
                  style={{ ...s.btn }}>{saving === '__new__' ? 'Erstellt…' : '✓ Anlegen'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal: Paket-Module */}
      {editPkg && (
        <div style={s.overlay} onClick={e => e.target === e.currentTarget && setEditPkg(null)}>
          <div style={s.mbox}>
            <div style={{ color: 'var(--admin-text)', fontWeight: 600, marginBottom: 16 }}>
              Module für: {packages.find(p => p.id === editPkg.id)?.label}
            </div>
            {modules.map(mod => (
              <label key={mod.name} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, cursor: 'pointer', color: 'var(--admin-text)', fontSize: '.83rem' }}>
                <input type="checkbox"
                  checked={editPkg.modules.includes(mod.name)}
                  onChange={() => setEditPkg(p => ({
                    ...p,
                    modules: p.modules.includes(mod.name)
                      ? p.modules.filter(m => m !== mod.name)
                      : [...p.modules, mod.name],
                  }))}
                />
                {mod.label}
                <code style={{ color: 'var(--admin-text-dim)', fontSize: '.7rem' }}>({mod.name})</code>
              </label>
            ))}
            {pkgSaveError && <div style={{ color: 'var(--admin-danger)', fontSize: '.73rem', marginTop: 8 }}>{pkgSaveError}</div>}
            <div style={{ display: 'flex', gap: 8, marginTop: 20, justifyContent: 'flex-end' }}>
              <button onClick={() => { setEditPkg(null); setPkgSaveError(''); }}
                style={{ background: 'none', border: '1px solid var(--admin-border)', color: 'var(--admin-text-muted)', borderRadius: 5, padding: '7px 16px', fontSize: '.75rem', cursor: 'pointer' }}>Abbrechen</button>
              <button onClick={handleSavePkg}
                style={{ ...s.btn }}>✓ Speichern</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
